import React, { useEffect, useMemo, useState } from 'react';
import { Select, Table } from '@/components/common/atoms';
import { BREAKPOINTS, Container, Flex } from '@/ui';
import { Text } from '@/ui/typography';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminKYCApi } from '@/api/admin/kyc';
import { kycApi } from '@/api/kyc';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { usePopups } from '@/hooks';

export const TableDocument = ({
  data,
  onClick,
  adminView = false,
  investorId,
  onDelete,
  isDocumentsFromQuestionnaires = false
}) => {
  const { t } = useTranslation();
  const { showInfoToast } = usePopups();
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const { data: sections } = useQuery({
    queryKey: ['getDocumentsSections'],
    queryFn: adminView
      ? () => adminKYCApi.getDocumentsSections(investorId)
      : kycApi.getDocumentsSections,
    enabled: !isDocumentsFromQuestionnaires
  });
  const { data: types, mutateAsync: fetchTypes } = useMutation({
    mutationFn: adminView ? adminKYCApi.getDocumentSectionTypes : kycApi.getDocumentSectionTypes,
    enabled: !isDocumentsFromQuestionnaires
  });
  const docsSectionsList = useMemo(() => (sections ? sections.data : null), [sections]);
  const docsTypesList = useMemo(() => (types ? types.data : null), [types]);

  useEffect(() => {
    if (selectedSection) {
      (async () => {
        try {
          await fetchTypes({
            sectionId: docsSectionsList?.find((section) => section.id === selectedSection)?.id,
            investorId: investorId
          });
          setSelectedType(null);
        } catch (e) {
          console.error('FETCH_DOCS_TYPES', e);
          showInfoToast(e, true);
        }
      })();
    }
  }, [selectedSection, docsSectionsList]);

  const tableData = [];

  data.map((item) => tableData.push(item.docs));

  const docsStatusObj = [];

  tableData.flat().map((item) => {
    docsStatusObj.push(item);
  });

  const filteredDocsStatusObj = useMemo(() => {
    let filteredArray = docsStatusObj;
    if (selectedSection) {
      filteredArray = docsStatusObj.filter(
        (item) => item.kycDocumentSection.id === selectedSection
      );
    }

    if (selectedType) {
      filteredArray = filteredArray.filter((item) => item.kycTypeDocument.id === selectedType);
    }

    return filteredArray;
  }, [docsStatusObj, selectedSection, selectedType]);

  return (
    <>
      {!isDocumentsFromQuestionnaires ? (
        <StyledFilterRow $marginBottom={32} gap={24}>
          <Container $maxWidth={328} $wide>
            <Container $marginBottom={8}>
              <Text $fontSize={14} $fontWeight={700}>
                {t('Section')}
              </Text>
            </Container>

            <Select
              allowClear
              placeholder={t('Select an item')}
              onChange={setSelectedSection}
              disabled={!docsSectionsList}
              value={selectedSection}
              options={
                docsSectionsList
                  ? docsSectionsList.map((item) => {
                      return { value: item.id, label: String(item.name).toLocaleLowerCase() };
                    })
                  : []
              }
            />
          </Container>

          <Container $maxWidth={328} $wide>
            <Container $marginBottom={8}>
              <Text $fontSize={14} $fontWeight={700}>
                {t('Type')}
              </Text>
            </Container>

            <Select
              allowClear
              placeholder={t('Select an item')}
              value={selectedType}
              onChange={setSelectedType}
              disabled={!docsTypesList || docsTypesList.length === 0}
              options={
                docsTypesList
                  ? docsTypesList.map((item) => {
                      return { value: item.id, label: String(item.name).toLocaleLowerCase() };
                    })
                  : []
              }
            />
          </Container>
        </StyledFilterRow>
      ) : null}

      <Table
        data={filteredDocsStatusObj}
        adminView={adminView}
        onClick={onClick}
        isDocumentTable={true}
        onDelete={onDelete}
        isOpenButtonEnabled={true}
        isDocumentsFromQuestionnaires={isDocumentsFromQuestionnaires}
      />
    </>
  );
};

const StyledFilterRow = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    flex-wrap: wrap;
  }
`;
